import {
    StanFloatingMenu,
    StanFloatingMenuHandle,
} from '@/components/core-components/StanDropdown/FloatingMenu/StanFloatingMenu';
import { useStanRouter } from '@/hooks/useStanRouter';
import { createIconElement, stanAnalytics } from '@/lib';
import { useUser } from '@/queries/useUser';
import { useAuthStore } from '@/stores';
import React, { useRef } from 'react';
import pillStyles from './MenuBarPill.module.css';
import IcCirclePlus from '@/assets/icons/ic-plus-circle.svg';
import IcHelpCircle from '@/assets/icons/ic-help-circle.svg';
import IcLogout from '@/assets/icons/ic-log-out.svg';
import { StanDivider } from '@/components';
import { SCOPES } from '@/lib/constants';

export function AccountMenu() {
    const menuRef = useRef<StanFloatingMenuHandle>(null);
    const { user, isUserLoading } = useUser();
    const logout = useAuthStore(state => state.logout);
    const prepareToAddAccount = useAuthStore(state => state.prepareToAddAccount);
    const switchAccount = useAuthStore(state => state.switchAccount);
    const accounts = useAuthStore(state => state.accounts);
    const currentAccountKey = useAuthStore(state => state.currentAccountKey);
    const isAdminLoggedInAsUser = useAuthStore(state => state.isAdminLoggedInAsUser);
    const accountScopesInclude = useAuthStore(state => state.accountScopesInclude);
    const { push } = useStanRouter();

    const profileImage = user?.profile_image || 'images/default-user.svg';
    const username = user?.username || '';
    const email = user?.email || '';

    const addAccount = () => {
        stanAnalytics('accounts-add-click', {
            meta: { user_id: user?.user_id, username: user?.username },
            props: {
                total_accounts: Object.keys(accounts).length,
                user_ids: Object.keys(accounts).map(key => accounts[key]?.user_id),
            },
        });

        prepareToAddAccount();
        // Go to login
        push('/login');
    };
    const Img = React.memo(
        (p: React.ImgHTMLAttributes<HTMLImageElement>) => <img {...p} />,
        (a, b) => a.src === b.src && a.srcSet === b.srcSet // ignore other props if you want
    );
    const BareIcCirclePlus = createIconElement(IcCirclePlus, { className: 'mr-2 rounded-full' });
    const BareIcHelpCircle = createIconElement(IcHelpCircle, { className: 'mr-2 rounded-full' });
    const BareIcLogout = createIconElement(IcLogout, { className: 'mr-2 rounded-full' });

    if (isUserLoading) {
        return (
            <div className={`${pillStyles.container} ${pillStyles.plain}`}>
                <div className='bg-brand-soft size-9 animate-pulse rounded-full' />
                <div className='bg-brand-soft h-4 w-20 animate-pulse rounded' />
            </div>
        );
    }

    return (
        <>
            <StanFloatingMenu
                id='profile-menu'
                name='profile-menu'
                className={pillStyles['account-menu-wrapper']}
                placement='top-start'
                ref={menuRef}
                transitionOptions={{
                    slideDirection: 'right',
                    transformOrigin: 'left',
                    slideDistance: 20,
                }}
                trigger={
                    <div className={`${pillStyles.container} ${pillStyles.plain}`}>
                        {user && (
                            <img
                                src={user?.profile_image || ''}
                                width={36}
                                height={36}
                                alt='Profile Picture'
                                className='rounded-3xl object-cover'
                            />
                        )}
                        <span className={pillStyles['pill-text']}>{user?.username}</span>
                    </div>
                }
            >
                <div className='z-10 w-80 rounded-lg border-none p-4'>
                    <div className='mb-7 flex items-center justify-start font-bold'>
                        <Img src={profileImage} className='mr-3 size-14 rounded-full' />
                        <div className=''>
                            <p className='m-0 pl-0 text-xs text-[var(--color-semantic-fg-danger)]'>
                                {isAdminLoggedInAsUser() ? 'Temporary' : ''}
                            </p>
                            <p className='text-dark m-0 pl-0 font-bold'>{username}</p>
                            <p className='text-gray m-0 pl-0 text-xs font-medium'>{email}</p>
                        </div>
                    </div>

                    <div className='max-h-[calc(max(300px, 75vh)-240px)] overflow-x-hidden overflow-y-auto'>
                        {Object.entries(accounts).map(
                            ([key, data]) =>
                                key !== currentAccountKey && (
                                    <div
                                        key={key}
                                        className='group text-dark my-0 mb-7 flex cursor-pointer items-center justify-start rounded-lg px-0'
                                        onClick={() => {
                                            switchAccount(key);
                                            menuRef.current?.setMenu(false);
                                        }}
                                    >
                                        <Img
                                            src={data.image || 'images/default-user.svg'}
                                            className='group-hover:border-brand-main mr-3 size-10 rounded-full border-2 border-transparent p-0.25'
                                        />
                                        <div>
                                            <p className='m-0 pl-0 text-xs text-[var(--color-semantic-fg-danger)]'>
                                                {accountScopesInclude(SCOPES.ADMIN_IMPERSONATION, key)
                                                    ? 'Temporary'
                                                    : ''}
                                            </p>
                                            <p className='group-hover:text-brand-main text-caption-1-bold m-0 pl-0'>
                                                {data.username}
                                            </p>
                                            <p className='text-gray group-hover:text-brand-main text-caption-2-regular m-0 pl-0'>
                                                {data.email}
                                            </p>
                                        </div>
                                    </div>
                                )
                        )}
                        <div className='text-dark hover:text-brand-main my-0 mb-3 flex cursor-pointer items-center justify-start rounded-lg p-0 px-0 font-bold'>
                            {BareIcCirclePlus}
                            <p className='pl-0' onClick={() => addAccount()}>
                                Add Account
                            </p>
                        </div>
                        <div className='text-dark hover:text-brand-main my-0 mb-0 flex cursor-pointer items-center justify-start rounded-lg p-0 px-0 font-bold'>
                            {BareIcHelpCircle}
                            <p className='pl-0' onClick={() => push('/support')}>
                                Get Help
                            </p>
                        </div>
                    </div>

                    <StanDivider className='my-6' />
                    <div className='text-dark hover:text-brand-main my-0 mb-0 flex cursor-pointer items-center justify-start rounded-lg p-0 px-0 font-bold hover:bg-white'>
                        {BareIcLogout}
                        <p className='COL pl-0' onClick={() => logout()}>
                            Logout
                        </p>
                    </div>
                </div>
            </StanFloatingMenu>
        </>
    );
}
